import { LStorage } from '@shared/Storage';
import axios from 'axios';
import { Ajax2 } from '@shared/Ajax';


export interface IAuthToken {
    accessToken: string;
    idToken: string;
    refreshToken: string;
    expiresIn: number;
    createdAt?: number; // set by service here
}

// responses:
// 401 {"errorMessage":"User not authorized"} // wrong pwd
// 401 {"errorMessage":"Token expired. The Token has expired on ___."}
function fetchToken(username: string, password: string): Promise<any> {
    clearToken();

    return axios({ // not using Ajax wrapper because we should not intercept these calls
        url: kuura.getIamAddress() + '/auth/token',
        method: 'POST',
        timeout: 10000,
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
        },
        data: {
            grant_type: 'password',
            username,
            password
        }
    })
    .then(resp => resp?.data)
    .then(token => {
        if (token.accessToken) {
            token.createdAt = Date.now();
            storeToken(token);
        }
    })
    .catch(error => {
        if (error?.response?.status === 401) {
            LStorage.setItem('iamNotEnabled', 'true');
        }
    });
}

function refreshToken(): Promise<IAuthToken> {
    const token = getStoredToken();

    /* A call for token refresh does not return a new creation timestamp so we
       create pre-emptively a timestamp for the refreshed token */
    const newCreatedAt = Date.now();

    if (!token) {
        return Promise.reject({ errorMsg: 'No token' });
    }

    if (!token.refreshToken) {
        return Promise.reject({ errorMsg: 'No refresh token' });
    }

    return axios({
        url: kuura.getIamAddress() + '/auth/token',
        method: 'POST',
        timeout: 10000,
        params: {
            grant_type: 'refresh_token',
            refresh_token: token.refreshToken
        }
    })
    .then(resp => resp?.data)
    .then(resp => {
        if (resp.accessToken) {
            // Cognito uses one refresh token per session, so we need to keep the same refresh token stored after refreshing
            storeToken({
                ...token,
                ...resp,
                createdAt: newCreatedAt
            });
        }
        return resp;
    });
}

function getStoredToken(): IAuthToken | null {
    try {
        return JSON.parse(LStorage.getItem(Auth.STORAGE_KEY)) || null;
    } catch {}

    return null;
}

function clearToken() {
    LStorage.removeItem(Auth.STORAGE_KEY);
    LStorage.removeItem('iamNotEnabled');
}

function storeToken(token: IAuthToken) {
    LStorage.setItem(Auth.STORAGE_KEY, JSON.stringify(token));
}

function isTokenExpired(token: IAuthToken): boolean {
    return Date.now() >= token.createdAt + (token.expiresIn * 1000);
}


let refreshPromise: Promise<IAuthToken> = null;



async function fetchTokenFromServer() {
    const tokenPath = "/oauth2/id-token";


    const response = await Ajax2({
        url: '/kuura' + tokenPath,
        method: 'GET',
    });


    if (response.status === false) {
        return null;
    }

    if (response.token) {
        const token: IAuthToken = {
            accessToken: response.token,
            idToken: response.token,
            refreshToken: null,
            createdAt: Date.now(),
            expiresIn: 3600
        };

        storeToken(token);
        return token;
    }

    return null;
}


async function getToken(): Promise<IAuthToken> {
    if (!refreshPromise) {
        let token = getStoredToken();


        if (!token) {
            token = await fetchTokenFromServer();
        }

        if (token && !isTokenExpired(token)) {
            return token;
        }


        if (token && token.refreshToken) {
            refreshPromise = refreshToken();
        }
    }

    if (refreshPromise) {
        const refreshedToken = await refreshPromise;
        refreshPromise = null;
        return refreshedToken;
    }

    return null;
}


let userPrivilegesCache = null;
async function hasIamPrivilege(privilege: string): Promise<boolean> {
    if (Array.isArray(userPrivilegesCache)) {
        console.log('returning from cache');
        return Promise.resolve(userPrivilegesCache.includes(privilege))
    }

    let privileges = [];
    const req: any = {
        url: kuura.getIamAddress() + `/authorization/user-privileges`,
        method: 'get',
        useToken: true,
        contentType: 'urlencoded'
    }
    const globalPromise = Ajax2({ // fetch global user privileges
        ...req,
        data: {
            userUuid: kuura.getUserUuid()
        }
    });
    const orgPromise = Ajax2({ // fetch user privileges on current project
        ...req,
        data: {
            userUuid: kuura.getUserUuid(),
            organizationUuid: kuura.getProjectOrganization().uuid
        }
    });
    try {
        const results = await Promise.allSettled([globalPromise, orgPromise])
        results.forEach(result => {
            if (Array.isArray((result as any).value)) {
                privileges.push(...(result as any).value);
            }
        })
    } catch {
    }
    userPrivilegesCache = privileges.map(p => p.privilegeName);
    return userPrivilegesCache.includes(privilege);
}

export const Auth = {
    STORAGE_KEY: 'authToken',
    clearToken,
    fetchToken,
    refreshToken,
    getStoredToken,
    getToken,
    isTokenExpired,
    hasIamPrivilege
};
